import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';

import uuidv4 from 'uuid/v4';
import {
  addFirestopItemToCreate,
  closeAddNewFirestopsDialog,
  closeConfirmFirestopItemDeleteDialog,
  closeConfirmFirestopListDeleteDialog,
  closeCreateFirestopListDialog,
  closeEditFirestopItemDialog,
  closeFirestopAdditionDialog,
  closeFirestopLocationsOverviewDialog,
  closeViewAndEditFirestopListDialog,
  editFirestopAttributes,
  modifyFirestopAdditionAttribute,
  modifyFirestopItemToCreate,
  modifyFirestopListAttribute,
  openAddNewFirestopDialog,
  openConfirmFirestopItemDeleteDialog,
  openConfirmFirestopListDeleteDialog,
  openCreateFirestopListDialog,
  openEditFirestopItemDialog,
  openFirestopAdditionDialog,
  openFirestopList,
  openFirestopLocationsOverviewDialog,
  removeFirestopImageDescription,
  setFirestopImageDescription,
  setFirestopImageToAdd,
  setFirestopImageToDelete,
  setHighlightedFirestopItemId,
  setIsCreatingOrUpdatingFirestopData,
  setIsPrintingFirestopListPdf,
} from './actions/firestop-documentation.actions';
import { createFirestopAddition } from './thunks/createFirestopAddition.thunk';
import { createFirestopList } from './thunks/createFirestopList.thunk';
import { createNewFirestops } from './thunks/createNewFirestops.thunk';
import { deleteFirestopItem } from './thunks/deleteFirestopItem.thunk';
import { deleteFirestopList } from './thunks/deleteFirestopList.thunk';
import { loadProjectFirestopDocumentationData } from './thunks/loadProjectFirestopDocumentationData.thunk';
import { saveFirestopListChanges } from './thunks/saveFirestopListChanges.thunk';
import { updateFirestopItem } from './thunks/updateFirestopItem.thunk';

const firestopListsAdapter = createEntityAdapter();
const firestopItemsAdapter = createEntityAdapter();
const firestopImagesAdapter = createEntityAdapter();

const firestopDocumentationSlice = createSlice({
  name: 'firestopDocumentation',
  initialState: {
    firestopLists: firestopListsAdapter.getInitialState(),
    firestopItems: firestopItemsAdapter.getInitialState(),
    firestopImages: firestopImagesAdapter.getInitialState(),
    changeHistoryItemsByItemId: {},
    isCreateFirestopListDialogOpen: false,
    isAddNewFirestopDialogOpen: false,
    isEditingListAttributes: false,
    loading: false,
    isCreatingOrUpdatingFirestopData: false,
    isPrintingFirestopListPdf: false,
    firestopListAttributeModifications: {},
    firestopItemsToCreate: {},
    editedFirestopAttributes: {},
    firestopImagesToAdd: [],
    firestopEditImagesToDelete: [],
    firestopAdditionAttributes: {},
    selectedFirestopListId: '',
    isConfirmDeleteFirestopListDialogOpen: false,
    isDeletingFirestopList: false,
    selectedFirestopItemIdToDelete: '',
    selectedFirestopItemIdToEdit: '',
    selectedAdditionFirestopItemId: '',
    imageDescriptions: {},
    latestWorkedAtByResource: {},
    isFirestopLocationsOverviewDialogOpen: false,
    highlightedFirestopItemId: '',
  },
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(
        loadProjectFirestopDocumentationData.pending,
        (state, action) => {
          state.loading = true;
        }
      )
      .addCase(
        loadProjectFirestopDocumentationData.fulfilled,
        (state, action) => {
          const { firestopLists, firestopItems, firestopImages } =
            action.payload;
          firestopListsAdapter.setMany(state.firestopLists, firestopLists);
          firestopItemsAdapter.setMany(state.firestopItems, firestopItems);
          firestopImagesAdapter.setMany(state.firestopImages, firestopImages);

          state.loading = false;
        }
      )
      .addCase(addFirestopItemToCreate, (state, action) => {
        const newInitialFirestopItem = { id: uuidv4() };

        state.firestopItemsToCreate[newInitialFirestopItem.id] =
          newInitialFirestopItem;
      })
      .addCase(createFirestopList.pending, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = true;
      })
      .addCase(createFirestopList.fulfilled, (state, action) => {
        const { firestopList } = action.payload;
        firestopListsAdapter.setOne(state.firestopLists, firestopList);

        state.selectedFirestopListId = firestopList.id;

        state.isCreateFirestopListDialogOpen = false;
        state.isCreatingOrUpdatingFirestopData = false;
      })
      .addCase(createFirestopList.rejected, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = false;
        enqueueSnackbar('Virhe palokatkodokumentaation luonnissa', {
          variant: 'error',
        });
      })
      .addCase(createNewFirestops.pending, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = true;
      })
      .addCase(createNewFirestops.fulfilled, (state, action) => {
        const { firestopItems, addedFirestopImages } = action.payload;
        firestopItemsAdapter.setMany(state.firestopItems, firestopItems);

        firestopImagesAdapter.setMany(
          state.firestopImages,
          addedFirestopImages
        );

        state.isCreatingOrUpdatingFirestopData = false;

        enqueueSnackbar('Palokatko luotu', {
          variant: 'success',
        });

        state.firestopImagesToAdd = [];
        state.imageDescriptions = {};
      })
      .addCase(createNewFirestops.rejected, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = false;
      })
      .addCase(openCreateFirestopListDialog, (state, action) => {
        const id = uuidv4();
        state.isCreateFirestopListDialogOpen = true;
        state.firestopItemsToCreate[id] = { id };
      })
      .addCase(closeCreateFirestopListDialog, (state, action) => {
        state.isCreateFirestopListDialogOpen = false;
        state.firestopItemsToCreate = {};
        state.imageDescriptions = {};
      })
      .addCase(modifyFirestopItemToCreate, (state, action) => {
        const { id, attribute, value } = action.payload;

        state.firestopItemsToCreate[id] = {
          ...state.firestopItemsToCreate[id],
          [attribute]: value,
        };
      })
      .addCase(editFirestopAttributes, (state, action) => {
        const { attribute, value } = action.payload;

        state.editedFirestopAttributes[attribute] = value;
      })
      .addCase(openFirestopList, (state, action) => {
        state.selectedFirestopListId = action.payload;
      })
      .addCase(closeViewAndEditFirestopListDialog, (state, action) => {
        state.selectedFirestopListId = '';
        state.firestopListAttributeModifications = {};
        state.isEditingListAttributes = false;
      })
      .addCase(modifyFirestopListAttribute, (state, action) => {
        state.firestopListAttributeModifications = {
          ...state.firestopListAttributeModifications,
          ...action.payload,
        };
      })
      .addCase(saveFirestopListChanges.fulfilled, (state, action) => {
        const { updatedFirestopListItem } = action.payload;

        firestopListsAdapter.upsertOne(
          state.firestopLists,
          updatedFirestopListItem
        );
        state.firestopListAttributeModifications = {};
        state.isEditingListAttributes = false;
      })
      .addCase(openAddNewFirestopDialog, (state, action) => {
        const id = uuidv4();
        state.isAddNewFirestopDialogOpen = true;
        state.firestopItemsToCreate[id] = { id };
      })
      .addCase(closeAddNewFirestopsDialog, (state, action) => {
        state.isAddNewFirestopDialogOpen = false;
        state.firestopItemsToCreate = {};
        state.imageDescriptions = {};
      })
      .addCase(openConfirmFirestopItemDeleteDialog, (state, action) => {
        state.selectedFirestopItemIdToDelete = action.payload;
      })
      .addCase(closeConfirmFirestopItemDeleteDialog, (state, action) => {
        state.selectedFirestopItemIdToDelete = '';
      })
      .addCase(deleteFirestopItem.fulfilled, (state, action) => {
        firestopItemsAdapter.removeOne(state.firestopItems, action.payload.id);
        state.selectedFirestopItemIdToDelete = '';
      })
      .addCase(openEditFirestopItemDialog, (state, action) => {
        state.selectedFirestopItemIdToEdit = action.payload;
      })
      .addCase(closeEditFirestopItemDialog, (state, action) => {
        state.selectedFirestopItemIdToEdit = '';
        state.editedFirestopAttributes = {};
        state.firestopImagesToAdd = [];
        state.firestopEditImagesToDelete = [];
        state.imageDescriptions = {};
      })
      .addCase(setFirestopImageToDelete, (state, action) => {
        state.firestopEditImagesToDelete.push(action.payload);
      })
      .addCase(updateFirestopItem.pending, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = true;
      })
      .addCase(updateFirestopItem.fulfilled, (state, action) => {
        const {
          updatedFirestopItem,
          addedFirestopImages,
          deletedFirestopImages,
        } = action.payload;

        firestopItemsAdapter.setOne(state.firestopItems, updatedFirestopItem);

        if (addedFirestopImages && addedFirestopImages.length > 0) {
          firestopImagesAdapter.setMany(
            state.firestopImages,
            addedFirestopImages
          );
        }

        if (deletedFirestopImages && deletedFirestopImages.length > 0) {
          const removedIds = deletedFirestopImages.map(
            (firestopImage) => firestopImage.id
          );
          firestopImagesAdapter.removeMany(state.firestopImages, removedIds);
        }

        state.firestopImagesToAdd = [];
        state.selectedFirestopItemIdToEdit = '';
        state.firestopEditImagesToDelete = [];
        state.editedFirestopAttributes = {};
        state.isCreatingOrUpdatingFirestopData = false;
        state.imageDescriptions = {};

        enqueueSnackbar('Palokatko päivitetty onnistuneesti', {
          variant: 'success',
        });
      })
      .addCase(updateFirestopItem.rejected, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = false;
        enqueueSnackbar('Virhe palokatkon päivityksessä', {
          variant: 'error',
        });
      })
      .addCase(setFirestopImageToAdd, (state, action) => {
        state.firestopImagesToAdd.push(action.payload);
      })
      .addCase(modifyFirestopAdditionAttribute, (state, action) => {
        const { attribute, value } = action.payload;

        state.firestopAdditionAttributes = {
          ...state.firestopAdditionAttributes,
          [attribute]: value,
        };
      })
      .addCase(createFirestopAddition.pending, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = true;
      })
      .addCase(createFirestopAddition.fulfilled, (state, action) => {
        const { firestopItem, addedFirestopImages } = action.payload;

        firestopItemsAdapter.setOne(state.firestopItems, firestopItem);
        firestopImagesAdapter.setMany(
          state.firestopImages,
          addedFirestopImages
        );

        state.selectedAdditionFirestopItemId = '';
        state.firestopAdditionAttributes = {};
        state.isCreatingOrUpdatingFirestopData = false;
        state.imageDescriptions = {};
        state.firestopImagesToAdd = [];

        enqueueSnackbar('Palokatko päivitetty onnistuneesti', {
          variant: 'success',
        });
      })
      .addCase(createFirestopAddition.rejected, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = false;
        enqueueSnackbar('Virhe palokatkon päivityksessä', {
          variant: 'error',
        });
      })
      .addCase(setIsCreatingOrUpdatingFirestopData, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = action.payload;
      })
      .addCase(setFirestopImageDescription, (state, action) => {
        const { firestopItemId, description, index } = action.payload;

        const currentFirestopImageDescriptions =
          state.imageDescriptions[firestopItemId];

        state.imageDescriptions = {
          ...state.imageDescriptions,
          [firestopItemId]: {
            ...currentFirestopImageDescriptions,
            [index]: description,
          },
        };
      })
      .addCase(removeFirestopImageDescription, (state, action) => {
        const { firestopItemId, index } = action.payload;
        const currentFirestopImagesDescriptions =
          state.imageDescriptions[firestopItemId];

        if (currentFirestopImagesDescriptions) {
          delete currentFirestopImagesDescriptions[index];
        }
      })
      .addCase(openFirestopAdditionDialog, (state, action) => {
        state.selectedAdditionFirestopItemId = action.payload;
      })
      .addCase(closeFirestopAdditionDialog, (state, action) => {
        state.selectedAdditionFirestopItemId = '';
      })
      .addCase(setIsPrintingFirestopListPdf, (state, action) => {
        state.isPrintingFirestopListPdf = action.payload;
      })
      .addCase(deleteFirestopList.pending, (state, action) => {
        state.isDeletingFirestopList = true;
      })
      .addCase(deleteFirestopList.fulfilled, (state, action) => {
        const {
          deletedFirestopItems,
          deletedFirestopImages,
          deletedFirestopListItem,
        } = action.payload;

        if (deletedFirestopListItem) {
          firestopListsAdapter.upsertOne(
            state.firestopLists,
            deletedFirestopListItem
          );
        }

        if (deletedFirestopItems && deletedFirestopItems.length > 0) {
          firestopItemsAdapter.upsertMany(
            state.firestopItems,
            deletedFirestopItems
          );
        }

        if (deletedFirestopImages && deletedFirestopImages.length > 0) {
          firestopImagesAdapter.upsertMany(
            state.firestopImages,
            deletedFirestopImages
          );
        }

        state.isConfirmDeleteFirestopListDialogOpen = false;
        state.selectedFirestopListId = '';
        state.isDeletingFirestopList = false;

        enqueueSnackbar('Palokatkodokumentaatio poistettu onnistuneesti', {
          variant: 'success',
        });
      })
      .addCase(deleteFirestopList.rejected, (state, action) => {
        state.isDeletingFirestopList = false;
        enqueueSnackbar('Virhe palokatkodokumentaation poistossa', {
          variant: 'error',
        });
      })
      .addCase(openConfirmFirestopListDeleteDialog, (state, action) => {
        state.isConfirmDeleteFirestopListDialogOpen = true;
      })
      .addCase(closeConfirmFirestopListDeleteDialog, (state, action) => {
        state.isConfirmDeleteFirestopListDialogOpen = false;
      })
      .addCase(openFirestopLocationsOverviewDialog, (state, action) => {
        state.isFirestopLocationsOverviewDialogOpen = true;
      })
      .addCase(closeFirestopLocationsOverviewDialog, (state, action) => {
        state.isFirestopLocationsOverviewDialogOpen = false;
      })
      .addCase(setHighlightedFirestopItemId, (state, action) => {
        state.highlightedFirestopItemId = action.payload;
      });
  },
});

export default firestopDocumentationSlice.reducer;
